import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CreateResidentSchema, CreateResidentInput } from '@hostel-manager/shared';
import { Bed } from '@hostel-manager/shared';
import { useFormValidation } from '../../hooks/useFormValidation';
import toast from 'react-hot-toast';

export interface ResidentFormProps {
  availableBeds: Bed[];
  onSubmit: (data: CreateResidentInput) => Promise<void>;
  onCancel: () => void;
}

const initialFormState = {
  bedNumber: '',
  name: '',
  contactNumber: '',
  emergencyContact: '',
  aadharNumber: '',
  address: '',
  admissionDate: new Date().toISOString().split('T')[0],
  joiningDate: new Date().toISOString().split('T')[0],
  depositPaidDate: new Date().toISOString().split('T')[0],
  monthlyRent: 7500,
  depositAmount: 15000,
  remarks: '',
};

/**
 * Resident admission form with client-side validation.
 * Uses shared Zod schema for consistent validation with server (FR-034).
 */
const ResidentForm: React.FC<ResidentFormProps> = ({ availableBeds, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const { validate } = useFormValidation(CreateResidentSchema, {
    showToastOnError: true,
    toastDuration: 5000,
  });

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === 'number' ? value : String(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prepare data for validation - convert numbers
    const dataToValidate = {
      ...formData,
      monthlyRent: Number(formData.monthlyRent),
      depositAmount: Number(formData.depositAmount),
    };

    const result = validate(dataToValidate);
    if (!result.valid || !result.data) {
      return; // Toast already shown by useFormValidation
    }

    setSubmitting(true);
    try {
      await onSubmit(result.data);
      toast.success('Resident admitted successfully');
      setFormData(initialFormState);
      onCancel();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to admit resident');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Bed Number</InputLabel>
            <Select
              value={formData.bedNumber}
              label="Bed Number"
              onChange={(e) => setFormData({ ...formData, bedNumber: e.target.value })}
            >
              {availableBeds.map((bed) => (
                <MenuItem key={bed.bedNumber} value={bed.bedNumber}>
                  {bed.bedNumber} - {bed.roomLocation} (₹{bed.rentAmount.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Full Name"
            value={formData.name}
            onChange={handleChange('name')}
            placeholder="Enter full name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Contact Number"
            value={formData.contactNumber}
            onChange={handleChange('contactNumber')}
            placeholder="+919876543210"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Emergency Contact"
            value={formData.emergencyContact}
            onChange={handleChange('emergencyContact')}
            placeholder="+919876543211"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Aadhar Number"
            value={formData.aadharNumber}
            onChange={handleChange('aadharNumber')}
            placeholder="1234 5678 9012"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Monthly Rent (₹)"
            type="number"
            value={formData.monthlyRent}
            onChange={handleChange('monthlyRent')}
            inputProps={{ min: 1000, max: 50000 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            multiline
            rows={2}
            value={formData.address}
            onChange={handleChange('address')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            label="Admission Date"
            type="date"
            value={formData.admissionDate}
            onChange={handleChange('admissionDate')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            label="Joining Date"
            type="date"
            value={formData.joiningDate}
            onChange={handleChange('joiningDate')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            label="Deposit Amount (₹)"
            type="number"
            value={formData.depositAmount}
            onChange={handleChange('depositAmount')}
            inputProps={{ min: 0, max: 100000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Deposit Paid Date"
            type="date"
            value={formData.depositPaidDate}
            onChange={handleChange('depositPaidDate')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={2}
            value={formData.remarks}
            onChange={handleChange('remarks')}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'flex-end' }}>
        <Grid item>
          <Button onClick={onCancel} disabled={submitting} sx={{ minHeight: 44 }}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !formData.name || !formData.bedNumber || !formData.contactNumber}
            sx={{ minHeight: 44 }}
          >
            {submitting ? 'Admitting...' : 'Admit Resident'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ResidentForm;
